import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import type { Page } from '../lib/api';
import type { AlertView } from '../lib/types';
import { useResource } from '../lib/useResource';
import { useSession } from '../lib/session';
import { Card, Empty, Loading, Notice, Pill, StatusPill, exactly, when } from '../components/ui';

export function Alerts() {
  const { can } = useSession();
  const [status, setStatus] = useState('OPEN');
  const [severity, setSeverity] = useState('');
  const [selected, setSelected] = useState<AlertView | null>(null);

  const alerts = useResource(
    () =>
      api.get<Page<AlertView>>('/api/v1/alerts', {
        status: status || undefined,
        severity: severity || undefined,
        size: 50,
      }),
    [status, severity],
  );

  return (
    <>
      <div className="page-head">
        <h1>Alerts</h1>
        <p>Findings raised by the detection rules against endpoint telemetry.</p>
      </div>

      <Card>
        <div className="filters">
          <label className="field">
            <span>Status</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">Any</option>
              {['OPEN', 'ACKNOWLEDGED', 'RESOLVED', 'DISMISSED'].map((value) => (
                <option key={value} value={value}>{value.toLowerCase()}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Severity</span>
            <select value={severity} onChange={(event) => setSeverity(event.target.value)}>
              <option value="">Any</option>
              {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'].map((value) => (
                <option key={value} value={value}>{value.toLowerCase()}</option>
              ))}
            </select>
          </label>
        </div>

        {alerts.error && <Notice kind="error">{alerts.error}</Notice>}
        {alerts.loading && !alerts.data && <Loading />}
        {alerts.data?.content.length === 0 && <Empty>No alerts match these filters.</Empty>}

        {alerts.data && alerts.data.content.length > 0 && (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Severity</th><th>Finding</th><th>Device</th><th className="num">Seen</th>
                  <th>Status</th><th>Last</th><th></th>
                </tr>
              </thead>
              <tbody>
                {alerts.data.content.map((alert) => (
                  <tr key={alert.id}>
                    <td><StatusPill value={alert.severity} /></td>
                    <td>
                      <strong>{alert.title}</strong>
                      <div style={{ color: 'var(--faint)', fontSize: 12 }} className="mono">{alert.ruleCode}</div>
                    </td>
                    <td>
                      {alert.deviceId
                        ? <Link to={`/devices/${alert.deviceId}`}>{alert.assetTag ?? 'device'}</Link>
                        : '—'}
                      {alert.hrRelevant && (
                        <div style={{ marginTop: 3 }}>
                          <Pill tone={alert.hrReleasedAt ? 'warn' : 'neutral'}>
                            {alert.hrReleasedAt ? 'released to HR' : 'HR-relevant'}
                          </Pill>
                        </div>
                      )}
                    </td>
                    <td className="num">{alert.occurrenceCount}</td>
                    <td><StatusPill value={alert.status} /></td>
                    <td>{when(alert.lastSeenAt)}</td>
                    <td>
                      <button type="button" className="secondary small" onClick={() => setSelected(alert)}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected && (
        <AlertDetail
          alert={selected}
          canTriage={can('TECH_ADMIN', 'SEC_ANALYST')}
          canRelease={can('TECH_ADMIN')}
          onChanged={() => {
            alerts.reload();
            setSelected(null);
          }}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

function AlertDetail({
  alert, canTriage, canRelease, onChanged, onClose,
}: {
  alert: AlertView;
  canTriage: boolean;
  canRelease: boolean;
  onChanged: () => void;
  onClose: () => void;
}) {
  const [note, setNote] = useState('');
  const [justification, setJustification] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function act(run: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await run();
      onChanged();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'The action failed');
      setBusy(false);
    }
  }

  return (
    <Card title={alert.title} extra={<button type="button" className="secondary small" onClick={onClose}>Close</button>}>
      {error && <Notice kind="error">{error}</Notice>}

      <dl className="kv" style={{ marginBottom: 14 }}>
        <dt>Rule</dt><dd className="mono">{alert.ruleCode}</dd>
        <dt>Summary</dt><dd>{alert.summary ?? '—'}</dd>
        <dt>Device</dt>
        <dd>{alert.deviceId ? <Link to={`/devices/${alert.deviceId}`}>{alert.assetTag}</Link> : '—'}</dd>
        <dt>Held by</dt><dd>{alert.assignedStaffName ?? 'unassigned'}</dd>
        <dt>Occurrences</dt><dd>{alert.occurrenceCount}</dd>
        <dt>First seen</dt><dd>{exactly(alert.firstSeenAt)}</dd>
        <dt>Last seen</dt><dd>{exactly(alert.lastSeenAt)}</dd>
      </dl>

      <details style={{ marginBottom: 14 }}>
        <summary style={{ cursor: 'pointer', fontSize: 13, color: 'var(--info)' }}>Evidence</summary>
        <pre className="evidence" style={{ marginTop: 10 }}>{JSON.stringify(alert.evidence, null, 2)}</pre>
      </details>

      {canTriage && alert.status !== 'RESOLVED' && alert.status !== 'DISMISSED' && (
        <>
          <label className="field">
            <span>Resolution note</span>
            <textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={2000} />
          </label>
          <div className="actions" style={{ marginBottom: 16 }}>
            <button
              type="button"
              disabled={busy}
              onClick={() => act(() => api.post(`/api/v1/alerts/${alert.id}/status`, { status: 'ACKNOWLEDGED', note }))}
            >
              Acknowledge
            </button>
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => act(() => api.post(`/api/v1/alerts/${alert.id}/status`, { status: 'RESOLVED', note }))}
            >
              Resolve
            </button>
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => act(() => api.post(`/api/v1/alerts/${alert.id}/status`, { status: 'DISMISSED', note }))}
            >
              Dismiss
            </button>
          </div>
        </>
      )}

      {alert.hrRelevant && canRelease && !alert.hrReleasedAt && (
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 14 }}>
          <Notice kind="warn">
            Releasing this finding makes it visible to HR. Activity-derived findings cannot on their
            own support an employment decision; releasing starts a review under the disciplinary
            procedure, with HR and Legal involved before any action.
          </Notice>
          <label className="field">
            <span>
              Why this should go to HR
              <span className="hint">Recorded permanently against your name in the audit trail.</span>
            </span>
            <textarea
              value={justification}
              onChange={(event) => setJustification(event.target.value)}
              maxLength={2000}
            />
          </label>
          <button
            type="button"
            className="danger"
            disabled={busy || justification.trim().length < 10}
            onClick={() => act(() => api.post(`/api/v1/alerts/${alert.id}/release-to-hr`, { justification }))}
          >
            Release to HR
          </button>
        </div>
      )}
    </Card>
  );
}
